import { BusinessObjectBase, decodeDecimals } from './base';
import { matchSchema } from '@schemas/business-objects/match';
import type { GroupMatchEntry } from './group-match-entry';
import type { Horse } from './horse';
import type { MatchResult } from './match-result';
import type { User } from './user';
import type { CalculationVersion, Id, Influence } from '../base-types';
import { pdfRenderResultSchema } from '@schemas/interface-definitions/pdf-render-result';
import type {
  HorseMeasurements,
  PdfRenderResult,
} from '../interface-definitions/index';

export interface MatchData {
  readonly id: Id;
  readonly mareMeasurements: HorseMeasurements;
  readonly stallionMeasurements: HorseMeasurements;
  readonly mareSizeInfluence: Influence;
  readonly sirePatternInfluence: Influence;
  readonly seed?: number;
  readonly calculationVersion: CalculationVersion;
  readonly runTimestamp: Date;
  readonly legacyMasterId?: number;
  readonly mareId: Id;
  readonly mare?: Horse;
  readonly stallionId: Id;
  readonly stallion?: Horse;
  readonly runById: Id;
  readonly runBy?: User;
  readonly results?: MatchResult[];
  readonly groupMatchEntries?: GroupMatchEntry[];
}

export interface Match extends Readonly<MatchData> {}
type MatchCreateWithChildrenData = Partial<Omit<MatchData, 'results'>> & {
  results?: Partial<MatchResult>[];
};

export class Match extends BusinessObjectBase {
  static readonly entityName = 'Match' as const;
  static readonly plural = 'matches';
  static schema = matchSchema;

  // --- Static CRUD methods ---

  static async find(
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<Match[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params, undefined, options);
    return results.map((data) => new Match(data));
  }

  static async findOne(
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<Match | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params, undefined, options);
    return data ? new Match(data) : null;
  }

  static async findById(
    id: Id,
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<Match> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params, undefined, options);
    return new Match(data);
  }

  static async create(data: Omit<MatchData, 'id'>): Promise<Match> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Match(result);
  }

  static async createMany(data: Omit<MatchData, 'id'>[]): Promise<Match[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Match(item));
  }

  static async createWithChildren(
    data: MatchCreateWithChildrenData,
  ): Promise<Match> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new Match(result);
  }

  static async createManyWithChildren(
    data: MatchCreateWithChildrenData[],
  ): Promise<Match[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Match(item));
  }

  static async updateById(id: Id, updates: Partial<MatchData>): Promise<Match> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Match(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  static async countMatching(where?: Record<string, any>): Promise<number> {
    const url = `${this.baseUrl}/${this.plural}/count-matching`;
    return this.post<number>(url, { where });
  }
  async reportPdf(): Promise<PdfRenderResult> {
    const url = `${BusinessObjectBase.baseUrl}/${Match.plural}/${(this as any).id}/report-pdf`;
    return BusinessObjectBase.get<PdfRenderResult>(url).then((value) =>
      decodeDecimals(pdfRenderResultSchema, value),
    );
  }
  static async run(
    mareId: number,
    stallionId: number,
    mareSizeInfluence: number,
    sirePatternInfluence: number,
    seed?: number,
  ): Promise<Match> {
    const url = `${this.baseUrl}/${this.plural}/run`;
    return this.post<Match>(url, {
      mareId,
      stallionId,
      mareSizeInfluence,
      sirePatternInfluence,
      seed,
    }).then((value) => decodeDecimals(matchSchema, value));
  }
}
